import React, { useState, useEffect, useMemo } from 'react'
import './EvaluationTokenUsage.css'

const TYPE_OPTIONS = [
  { value: 'cluster_report', text: 'Cluster/Report' },
  { value: 'evals', text: 'Evals' },
  { value: 'validate_sources', text: 'Sources' },
  { value: 'validate_agent_calls', text: 'Agent calls' }
]

const ENDPOINTS = {
  'bot.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-insights.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-insights.svc.stratsys.com',
  'localhost:8080': 'http://localhost:5001'
}

function EvaluationTokenUsage() {
  const [dates, setDates] = useState([])
  const [selectedTypes, setSelectedTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [tokenUsage, setTokenUsage] = useState([])

  /* ---------------- Fetch ---------------- */
  const fetchTokenUsage = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await window.PDR.api.post(`${ENDPOINTS[window.location.host]}/token_usage`, {
        date_range: dates,
        types: selectedTypes
      })
      setTokenUsage(response)
    } catch (e) {
      console.error(e)
      setError('Failed to fetch token usage data 🙈')
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- Data shaping ---------------- */

  // Group by run_id first
  const runs = useMemo(() => {
    const byRun = {}
    tokenUsage.forEach((rec) => {
      const runId = rec.run_id
      if (!byRun[runId]) {
        byRun[runId] = {
          run_id: runId,
          timestamp: rec.timestamp,
          perType: {},
          model: rec.model || 'No model provided' // default if not provided
        }
      }
      byRun[runId].perType[rec.type] = {
        prompt: rec.prompt_tokens,
        completion: rec.completion_tokens
      }
    })
    return Object.values(byRun)
  }, [tokenUsage])

  // …then bucket runs by date (YYYY-MM-DD)
  const runsByDate = useMemo(() => {
    const map = {}
    runs.forEach((run) => {
      const date = new Date(run.timestamp).toISOString().slice(0, 10)
      if (!map[date]) map[date] = []
      map[date].push(run)
    })
    // keep dates sorted ascending (oldest left, newest right)
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .reduce((acc, [d, arr]) => ({ ...acc, [d]: arr }), {})
  }, [runs])

  // Totals across all rows
  const grandTotals = useMemo(() => {
    const totals = { overallPrompt: 0, overallCompletion: 0 }
    TYPE_OPTIONS.forEach(({ value }) => {
      totals[value] = { prompt: 0, completion: 0 }
    })
    tokenUsage.forEach((rec) => {
      totals[rec.type].prompt += rec.prompt_tokens
      totals[rec.type].completion += rec.completion_tokens
      totals.overallPrompt += rec.prompt_tokens
      totals.overallCompletion += rec.completion_tokens
    })
    return totals
  }, [tokenUsage])

  /* ---------------- Render helpers ---------------- */

  const renderTotals = () => (
    <div className="totals-container">
      {TYPE_OPTIONS.map(({ value, text }) => (
        <div className="total-card" key={value}>
          <h4>{text}</h4>
          <div className="token-counts">
            <div className="token-count">
              <div>{grandTotals[value].prompt}</div>
              <div className="token-label">PROMPT</div>
            </div>
            <div className="token-arrow">➜</div>
            <div className="token-count">
              <div>{grandTotals[value].completion}</div>
              <div className="token-label">COMPLETION</div>
            </div>
          </div>
        </div>
      ))}
      <div className="total-card overall">
        <h3>Total</h3>
        <div className="token-counts">
          <div className="token-count">
            <div>{grandTotals.overallPrompt}</div>
            <div className="token-label">PROMPT</div>
          </div>
          <div className="token-arrow">➜</div>
          <div className="token-count">
            <div>{grandTotals.overallCompletion}</div>
            <div className="token-label">COMPLETION</div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderRunCard = (run) => (
    <div className="run-card" key={run.run_id}>
      <div className="run-card__header">
        <span className="run-id">{run.run_id}</span>
        <span className="run-time">
          {new Date(run.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </span>
      </div>
      <div className="run-card__model">
        <span className="model-label">Model:</span> {run.model || 'gpt-4o-mini (unverified)'}
      </div>

      <table className="token-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Prompt</th>
            <th>Completion</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(run.perType).map(([type, tok]) => (
            <tr key={type}>
              <td>{type}</td>
              <td>{tok.prompt}</td>
              <td>{tok.completion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const renderDateColumns = () => (
    <div className="date-columns">
      {Object.entries(runsByDate).map(([date, dateRuns]) => (
        <div className="date-column" key={date}>
          <div className="date-column__header">
            {new Date(date).toLocaleDateString(undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </div>
          {dateRuns.map(renderRunCard)}
        </div>
      ))}
    </div>
  )

  /* ---------------- UI ---------------- */
  return (
    <div className="evaluation-token-usage-page">
      {/* --- Header --- */}
      <div className="header">
        <img
          className="stratsys-search-logo"
          src="https://frontend-packages.svc.stratsys.com/logos/icon-dark.svg"
          alt="Stratsys Icon"
        />
        <div className="one-ux-typography--heading-500">
          <one-ux-text>Evaluation Token Usage</one-ux-text>
        </div>
      </div>

      {/* --- Filters --- */}
      <div className="filters">
        <one-ux-field label="Date range">
          <one-ux-dropdown placeholder="Filter">
            <one-ux-date-picker range="True" value={dates} onChange={(e) => setDates(e.target.value)} />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-field label="Type">
          <one-ux-dropdown placeholder="Select type">
            <one-ux-list
              multiple
              options={TYPE_OPTIONS}
              value={selectedTypes}
              onInput={(e) => setSelectedTypes(e.target.value)}
            />
          </one-ux-dropdown>
        </one-ux-field>

        <one-ux-button label="Fetch" purpose="main" weight="high" onClick={fetchTokenUsage}>
          <one-ux-icon icon="line-straight" set="chart" slot="start"></one-ux-icon>
        </one-ux-button>
      </div>

      {/* --- Content --- */}
      {loading && <div className="loader"></div>}
      {error && <div className="error_message">{error}</div>}
      {!loading && !error && tokenUsage.length > 0 && (
        <>
          {renderTotals()}
          {renderDateColumns()}
        </>
      )}
    </div>
  )
}

export default EvaluationTokenUsage
